import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { login as apiLogin, configuraAuth } from "./api";

const AuthContext = createContext(null);
const CHIAVE_TOKEN = "dbi_token";
const CHIAVE_USER = "dbi_username";
const CHIAVE_PUO_MAPPARE = "dbi_puo_mappare";
const CHIAVE_AZIENDA = "dbi_azienda";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(CHIAVE_TOKEN) || null; } catch (e) { return null; }
  });
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem(CHIAVE_USER) || null; } catch (e) { return null; }
  });
  // Se manca (login precedente a questa funzionalità, o localStorage non
  // disponibile) si presume "sì" — stesso default lato server (vedi
  // routes/auth.js puoUtenteMappare): la restrizione è un'eccezione esplicita,
  // mai un blocco silenzioso per chi ha già una sessione aperta.
  const [puoMappare, setPuoMappare] = useState(() => {
    try {
      const v = localStorage.getItem(CHIAVE_PUO_MAPPARE);
      return v === null ? true : v === "true";
    } catch (e) { return true; }
  });
  // Azienda (tenant) dell'utente: "" per wtrendy (vede tutto, non è di
  // nessuna azienda) o per un utente non ancora assegnato — vedi
  // routes/accessoAzienda.js lato server, stessa logica.
  const [azienda, setAzienda] = useState(() => {
    try { return localStorage.getItem(CHIAVE_AZIENDA) || ""; } catch (e) { return ""; }
  });

  const logout = useCallback(() => {
    setToken(null);
    setUsername(null);
    try {
      localStorage.removeItem(CHIAVE_TOKEN);
      localStorage.removeItem(CHIAVE_USER);
      localStorage.removeItem(CHIAVE_PUO_MAPPARE);
      localStorage.removeItem(CHIAVE_AZIENDA);
    } catch (e) { /* localStorage non disponibile */ }
    // L'HashRouter dentro Shell si smonta al logout ma l'hash nell'URL
    // (es. "#/distinta-base") resta quello dov'era: se non lo si azzera qui,
    // al prossimo login la Shell rimonta leggendo quell'hash vecchio e apre
    // direttamente quella pagina invece della scelta iniziale.
    window.location.hash = "/";
  }, []);

  // src/api.js legge il token direttamente da localStorage ad ogni chiamata
  // (niente corsa con l'effect qui sotto); questo effect serve solo a
  // registrare cosa fare se il server risponde 401 (token scaduto/non valido).
  useEffect(() => {
    configuraAuth({ onNonAutorizzato: logout });
  }, [logout]);

  const entra = useCallback(async (azienda, username, password) => {
    const dati = await apiLogin(azienda, username, password);
    // localStorage viene scritto PRIMA di aggiornare lo stato React: il
    // fetch di /api/tracciato che parte al mount della Shell (subito dopo
    // che autenticato diventa true) deve già trovarlo pronto.
    try {
      localStorage.setItem(CHIAVE_TOKEN, dati.token);
      localStorage.setItem(CHIAVE_USER, dati.username);
      localStorage.setItem(CHIAVE_PUO_MAPPARE, String(dati.puoMappare !== false));
      localStorage.setItem(CHIAVE_AZIENDA, dati.azienda || "");
    } catch (e) { /* localStorage non disponibile: resta valido solo per questa sessione */ }
    window.location.hash = "/"; // stesso motivo del logout: si riparte sempre dalla scelta iniziale
    setToken(dati.token);
    setUsername(dati.username);
    setPuoMappare(dati.puoMappare !== false);
    setAzienda(dati.azienda || "");
  }, []);

  const value = { token, username, puoMappare, azienda, autenticato: !!token, entra, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve essere usato dentro AuthProvider");
  return ctx;
}
